import React, { useEffect, useRef, useState } from 'react';
import { WithAgreeModal } from 'routes/components/AgreeModal';
import { useRouting } from 'hooks/useRouting';
import { debounce } from 'utils/debounce';
import FirstLoginModal from 'components/features/modal/FirstLoginModal';
import {
  isFirstTimeLogin,
  setLastLoginDateTime,
} from 'components/features/utils/utils';
import {
  isIosApp,
  isIosAppNotPremium,
} from 'components/features/utils/userStatus';

type Props = {
  disabled?: boolean;
};
const VideoPlayer: React.FC<Props> = ({ disabled = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [isPaused, setVideoPaused] = useState(false);
  const [hasPlayedOnce, setHasPlayed] = useState(false);

  useEffect(() => {
    videoRef?.current?.addEventListener('pause', () => setVideoPaused(true));
    videoRef?.current?.addEventListener('play', () => setVideoPaused(false));
    if (!disabled && !hasPlayedOnce) {
      videoRef?.current?.play().then(() => setHasPlayed(true));
    }
  });

  const handlePlayer = (): void => {
    const videoElement = videoRef?.current;
    videoElement?.paused && videoElement.play();
  };

  return (
    <div
      className={`my-4 mx-0 rounded flex items-center justify-center ${isPaused &&
        'cursor-pointer'}`}
      onClick={handlePlayer}>
      <video
        src={`/static/images/kouza-renkei-movie.mp4`}
        muted={true}
        playsInline={true}
        style={{ pointerEvents: 'none' }}
        ref={videoRef}
      />
    </div>
  );
};

const AccountLinkedInfo: React.FC = () => {
  const [isShowFirstTimeLoginModal, setIsShowModal] = useState<
    'ready' | 'showing' | 'showed' | 'need_not_showing'
  >('ready');

  useEffect(() => {
    const isFirstLogin = isFirstTimeLogin();
    setIsShowModal(isFirstLogin ? 'showing' : 'need_not_showing');
  }, []);

  const closeModal = (): void => {
    setLastLoginDateTime();
    setIsShowModal('showed');
  };

  const routing = useRouting();
  function handleClickSecurityAccountsLink(): void {
    routing.replaceRouting({ path: '/securityaccountslink' }, true);
  }

  // 追従ボタンがstickyか否かでスタイルが異なるため対応、要共通化
  useEffect(() => {
    const addGroupScrollDebounce = debounce(() => {
      const accountLinkedBtnPosition = document
        .getElementById('accountLinkedBtn')
        ?.getBoundingClientRect().bottom;
      const viewHeight = window.innerHeight;
      if (accountLinkedBtnPosition === undefined) {
        return null;
      } else if (accountLinkedBtnPosition === viewHeight) {
        document
          .getElementById('accountLinkedBtn')
          ?.classList.add('bg-op-w80', 'shadow-fBtn');
        document
          .getElementById('accountLinkedBtn')
          ?.classList.remove('bg-white');
      } else {
        document
          .getElementById('accountLinkedBtn')
          ?.classList.remove('bg-op-w80', 'shadow-fBtn');
        document.getElementById('accountLinkedBtn')?.classList.add('bg-white');
      }
    }, 16);
    window.addEventListener('scroll', addGroupScrollDebounce, true);

    return () => {
      window.removeEventListener('scroll', addGroupScrollDebounce, true);
    };
  }, []);

  return (
    <>
      <div className="first-login">
        <div className="text-lg font-bold leading-6 text-center m-4">
          はじめての方へ
          <br />
          まずは口座を連携してみましょう
        </div>
        <div className="bg-white px-6 pt-6">
          <div className="flex items-center justify-center text-bluegray-500 font-bold text-xs leading-8 bg-bluegray-50 rounded-3xl h-8 relative bubble-block">
            口座を連携すると？
          </div>
          <div className="flex flex-col justify-center items-center">
            <ul className="text-sm font-bold login-check-list">
              <li className="check-list-item">
                複数の証券口座にある資産の動きがわかる
              </li>
              <li className="check-list-item">
                LINEで投資の損益や最新の資産がわかる
              </li>
              <li className="check-list-item">取引履歴を分析できる</li>
              <li className="check-list-item">
                ライフプランに合わせて資産運用をシミュレーションできる
              </li>
            </ul>
          </div>
          {!isIosAppNotPremium() && (
            <VideoPlayer disabled={isShowFirstTimeLoginModal === 'showing'} />
          )}
          <div className="py-4 px-8 bg-gray-100 rounded">
            <div className="m-2 text-sm font-bold leading-5 text-center text-bluegray-700">
              連携可能な証券会社・サービス
            </div>
            <div className="leading-4 text-xs text-bluegray-500 text-center">
              {!isIosAppNotPremium() ? (
                <>
                  SBI証券 / 楽天証券 / 三菱UFJ eスマート証券 / 松井証券
                  <br />
                  Moneytree
                </>
              ) : (
                <>SBI証券</>
              )}
            </div>
          </div>
        </div>
        <div className="px-4 pt-4 text-bluegray-700 text-xs leading-4 text-center font-bold bg-white">
          ＼ 最短１分で連携完了 ／
        </div>
        {!isIosAppNotPremium() ? (
          <div
            id="accountLinkedBtn"
            className="sticky z-10 bottom-0 p-4 bg-op-w80 shadow-fBtn">
            <div
              className="ui-btn px-3 mx-5 text-sm font-bold h-10 leading-10 cursor-pointer"
              onClick={handleClickSecurityAccountsLink}>
              さっそく連携してみる
            </div>
          </div>
        ) : (
          <div className="z-10 bottom-0 px-4 pb-4 bg-white">
            <div
              className="ui-btn px-3 mx-5 text-sm font-bold h-10 leading-10"
              onClick={handleClickSecurityAccountsLink}>
              さっそく連携してみる
            </div>
          </div>
        )}
        {!isIosApp() && (
          <div className="ui-card rounded-none mt-8">
            <div className="p-4 border-b border-gray-200 font-bold">
              証券口座をお持ちでない方
            </div>
            <div className="ui-border-b p-2">
              <div className="ml-4 mr-2 flex items-center py-2">
                <div className="flex-grow">
                  <div className="text-lg font-bold">SBI証券</div>
                </div>
                <a
                  id="accountLinkageInfo_accoutOpening"
                  href="https://minkabu.jp/hikaku/redirect_tw/sbi.html?utm_source=tw&amp;utm_medium=securities_list&amp;utm_campaign=606010"
                  target="_blank"
                  rel="noopener noreferrer">
                  <div className="flex py-1 px-2 border rounded border-link text-link bg-white">
                    <span className="text-sm font-bold">口座開設</span>
                    <i className="icon-external-link ml-2 text-lg" />
                  </div>
                </a>
              </div>
            </div>
            <div className="ui-border-b p-2">
              <div className="ml-4 mr-2 flex items-center py-2">
                <div className="flex-grow">
                  <div className="text-lg font-bold">楽天証券</div>
                </div>
                <a
                  id="accountLinkageInfo_accoutOpening"
                  href="https://minkabu.jp/hikaku/redirect_tw/rakuten.html?utm_source=tw&amp;utm_medium=securities_list&amp;utm_campaign=606010"
                  target="_blank"
                  rel="noopener noreferrer">
                  <div className="flex py-1 px-2 border rounded border-link text-link bg-white">
                    <span className="text-sm font-bold">口座開設</span>
                    <i className="icon-external-link ml-2 text-lg" />
                  </div>
                </a>
              </div>
            </div>
            <div className="ui-border-b p-2">
              <div className="ml-4 mr-2 flex items-center py-2">
                <div className="flex-grow">
                  <div className="text-lg font-bold">松井証券</div>
                </div>
                <a
                  id="accountLinkageInfo_accoutOpening"
                  href="https://minkabu.jp/hikaku/redirect_tw/matsui.html?utm_source=tw&amp;utm_medium=securities_list&amp;utm_campaign=606010"
                  target="_blank"
                  rel="noopener noreferrer">
                  <div className="flex py-1 px-2 border rounded border-link text-link bg-white">
                    <span className="text-sm font-bold">口座開設</span>
                    <i className="icon-external-link ml-2 text-lg" />
                  </div>
                </a>
              </div>
            </div>
            <div className="ui-border-b p-2">
              <div className="ml-4 mr-2 flex items-center py-2">
                <div className="flex-grow">
                  <div className="text-lg font-bold">三菱UFJ eスマート証券</div>
                </div>
                <a
                  id="accountLinkageInfo_accoutOpening"
                  href="https://minkabu.jp/hikaku/redirect_tw/kabucom.html?utm_source=tw&amp;utm_medium=securities_list&amp;utm_campaign=606010"
                  target="_blank"
                  rel="noopener noreferrer">
                  <div className="flex py-1 px-2 border rounded border-link text-link bg-white">
                    <span className="text-sm font-bold">口座開設</span>
                    <i className="icon-external-link ml-2 text-lg" />
                  </div>
                </a>
              </div>
            </div>
            <div className="m-4 text-center">
              <a
                id="accountLinkageInfo_ranking"
                className="text-link text-sm"
                href="https://minkabu.jp/hikaku/lp/total_ranking_tw.html?utm_source=tw&amp;utm_medium=securities_list&amp;utm_campaign=606010"
                target="_blank"
                rel="noopener noreferrer">
                ネット証券 総合ランキングを見る
              </a>
            </div>
          </div>
        )}
      </div>
      <FirstLoginModal
        isShow={isShowFirstTimeLoginModal === 'showing'}
        close={closeModal}
      />
      {isShowFirstTimeLoginModal === 'showed' ||
      isShowFirstTimeLoginModal === 'need_not_showing' ? (
        <>
          <WithAgreeModal />
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default AccountLinkedInfo;
